import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { toCx, cx } from './classname.js';
import { mapUnboundedProp, isUnboundedValue } from './unboundedProps.js';
import { Direction, useLocaleContext } from '../contexts/session/Locale.js';
import { useThemeContext } from '../contexts/session/Theme.js';
import { wordBreakMap, whiteSpaceMap, visibilityMap, verticalAlignMap, userSelectMap, touchActionMap, textOverflowMap, textDecorationStyleMap, textDecorationLineMap, textDecorationMap, textAlignMap, scrollSnapTypeMap, scrollSnapStopMap, scrollSnapAlignMap, positionMap, pointerEventsMap, overflowYMap, overflowXMap, overflowScrollingMap, overflowMap, outlineStyleMap, listStyleMap, justifyContentMap, gridAutoRowsMap, gridAutoFlowMap, gridAutoColumnsMap, flexWrapMap, flexShrinkMap, flexGrowMap, flexDirectionMap, flexMap, displayMap, boxSizingMap, borderStyleMap, alignSelfMap, alignItemsMap, alignContentMap } from './mappers.mappings.js';
import { toMapper, mapTextColor, mapSafeArea, floatMapper, mapCursor, mapBorderRadius, mapBorderColor, mapBorder, mapBackground, mapStart, mapGradient, mapEnd, mapBackgroundImage, mapBackgroundColor } from './mappers.util.js';

var _excluded = ["type", "value"],
  _excluded2 = ["as"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var propertyToMapper = {
  adsClass: function adsClass(_adsClass) {
    return _adsClass !== null && _adsClass !== void 0 ? _adsClass : [];
  },
  alignContent: toMapper(alignContentMap),
  alignItems: toMapper(alignItemsMap),
  alignSelf: toMapper(alignSelfMap),
  backgroundColor: mapBackground,
  backgroundRepeat: function backgroundRepeat(_backgroundRepeat, _) {
    return toCx('bg', _backgroundRepeat);
  },
  backgroundSize: function backgroundSize(_backgroundSize, _) {
    return toCx('bg', _backgroundSize);
  },
  border: mapBorder,
  borderColor: function borderColor(borderType, _) {
    return mapBorderColor(borderType, _);
  },
  borderRadius: mapBorderRadius,
  borderStyle: toMapper(borderStyleMap),
  boxShadow: function boxShadow(shadowType, _) {
    return toCx('shadow', shadowType);
  },
  boxSizing: toMapper(boxSizingMap),
  className: function className(_className) {
    return _className;
  },
  columnGap: function columnGap(_columnGap, _) {
    return toCx('gap-x', _columnGap);
  },
  cursor: mapCursor,
  display: toMapper(displayMap),
  empty: function empty(_empty) {
    return _empty ? 'empty:hidden' : undefined;
  },
  filter: function filter(_filter, _) {
    return toCx('filter', _filter);
  },
  flex: toMapper(flexMap),
  flexDirection: toMapper(flexDirectionMap),
  flexGrow: toMapper(flexGrowMap),
  flexShrink: toMapper(flexShrinkMap),
  flexWrap: toMapper(flexWrapMap),
  float: floatMapper,
  gap: function gap(_gap, _) {
    return toCx('gap', _gap);
  },
  gridAutoColumns: toMapper(gridAutoColumnsMap),
  gridAutoFlow: toMapper(gridAutoFlowMap),
  gridAutoRows: toMapper(gridAutoRowsMap),
  height: function height(_height) {
    return mapUnboundedProp('height', _height);
  },
  justifyContent: toMapper(justifyContentMap),
  listStyle: toMapper(listStyleMap),
  m: function m(_m, _) {
    return toCx('m', _m);
  },
  mb: function mb(_mb, _) {
    return toCx('mb', _mb);
  },
  me: function me(_me, _ref) {
    var isLtr = _ref.isLtr;
    return toCx(isLtr ? 'mr' : 'ml', _me);
  },
  ms: function ms(_ms, _ref2) {
    var isLtr = _ref2.isLtr;
    return toCx(isLtr ? 'ml' : 'mr', _ms);
  },
  mt: function mt(_mt, _) {
    return toCx('mt', _mt);
  },
  mx: function mx(_mx, _) {
    return toCx('mx', _mx);
  },
  my: function my(_my, _) {
    return toCx('my', _my);
  },
  minHeight: function minHeight(_minHeight) {
    return mapUnboundedProp('minHeight', _minHeight);
  },
  minWidth: function minWidth(_minWidth) {
    return mapUnboundedProp('minWidth', _minWidth);
  },
  maxHeight: function maxHeight(_maxHeight) {
    return mapUnboundedProp('maxHeight', _maxHeight);
  },
  maxWidth: function maxWidth(_maxWidth) {
    return mapUnboundedProp('maxWidth', _maxWidth);
  },
  order: function order(_order) {
    return mapUnboundedProp('order', _order);
  },
  outlineColor: function outlineColor(_outlineColor, _) {
    return toCx('outline', _outlineColor);
  },
  outlineStyle: toMapper(outlineStyleMap),
  overflow: toMapper(overflowMap),
  overflowScrolling: toMapper(overflowScrollingMap),
  overflowX: toMapper(overflowXMap),
  overflowY: toMapper(overflowYMap),
  p: function p(_p, _) {
    return toCx('p', _p);
  },
  pb: function pb(_pb, _) {
    return toCx('pb', _pb);
  },
  pe: function pe(_pe, _ref3) {
    var isLtr = _ref3.isLtr;
    return toCx(isLtr ? 'pr' : 'pl', _pe);
  },
  ps: function ps(_ps, _ref4) {
    var isLtr = _ref4.isLtr;
    return toCx(isLtr ? 'pl' : 'pr', _ps);
  },
  pt: function pt(_pt, _) {
    return toCx('pt', _pt);
  },
  px: function px(_px, _) {
    return toCx('px', _px);
  },
  py: function py(_py, _) {
    return toCx('py', _py);
  },
  pointerEvents: toMapper(pointerEventsMap),
  position: toMapper(positionMap),
  rowGap: function rowGap(_rowGap, _) {
    return toCx('gap-y', _rowGap);
  },
  safeArea: mapSafeArea,
  scrollSnapAlign: toMapper(scrollSnapAlignMap),
  scrollSnapStop: toMapper(scrollSnapStopMap),
  scrollSnapType: toMapper(scrollSnapTypeMap),
  textAlign: toMapper(textAlignMap),
  textColor: function textColor(_textColor, _) {
    return [mapTextColor(_textColor, 'fill', _), mapTextColor(_textColor, 'text', _)];
  },
  textDecoration: toMapper(textDecorationMap),
  textDecorationLine: toMapper(textDecorationLineMap),
  textDecorationStyle: toMapper(textDecorationStyleMap),
  textOverflow: toMapper(textOverflowMap),
  touchAction: toMapper(touchActionMap),
  userSelect: toMapper(userSelectMap),
  verticalAlign: toMapper(verticalAlignMap),
  visibility: toMapper(visibilityMap),
  whiteSpace: toMapper(whiteSpaceMap),
  width: function width(_width) {
    return mapUnboundedProp('width', _width);
  },
  wordBreak: toMapper(wordBreakMap)
};
var stylePropertyToMapper = {
  DEPRECATED_gap: function DEPRECATED_gap(gap, _) {
    return {
      gap: gap
    };
  },
  WebkitMask: function WebkitMask(mask, _ref5) {
    var isLtr = _ref5.isLtr,
      intent = _ref5.intent;
    return mapGradient(mask, isLtr ? Direction.LTR : Direction.RTL, undefined, intent);
  },
  aspectRatio: function aspectRatio(_aspectRatio, _) {
    return _aspectRatio;
  },
  backgroundColor: mapBackgroundColor,
  backgroundImage: mapBackgroundImage,
  backgroundPosition: function backgroundPosition(_backgroundPosition, _) {
    return _backgroundPosition;
  },
  bottom: function bottom(_bottom, _) {
    return _bottom;
  },
  className: function className(_className2) {
    return _className2;
  },
  end: mapEnd,
  flexBasis: function flexBasis(_flexBasis, _) {
    return _flexBasis;
  },
  height: function height(_height2) {
    return isUnboundedValue('height', _height2) ? _height2 : undefined;
  },
  left: function left(_left, _) {
    return _left;
  },
  mask: function mask(_mask, _ref6) {
    var isLtr = _ref6.isLtr,
      intent = _ref6.intent;
    return mapGradient(_mask, isLtr ? Direction.LTR : Direction.RTL, undefined, intent);
  },
  maxHeight: function maxHeight(_maxHeight2) {
    return isUnboundedValue('maxHeight', _maxHeight2) ? _maxHeight2 : undefined;
  },
  maxWidth: function maxWidth(_maxWidth2) {
    return isUnboundedValue('maxWidth', _maxWidth2) ? _maxWidth2 : undefined;
  },
  minHeight: function minHeight(_minHeight2) {
    return isUnboundedValue('minHeight', _minHeight2) ? _minHeight2 : undefined;
  },
  minWidth: function minWidth(_minWidth2) {
    return isUnboundedValue('minWidth', _minWidth2) ? _minWidth2 : undefined;
  },
  opacity: function opacity(_opacity, _) {
    return _opacity;
  },
  order: function order(_order2) {
    return isUnboundedValue('order', _order2) ? _order2 : undefined;
  },
  right: function right(_right, _) {
    return _right;
  },
  start: mapStart,
  top: function top(_top, _) {
    return _top;
  },
  transform: function transform(_transform, _) {
    return _transform;
  },
  transition: function transition(_transition, _) {
    return _transition;
  },
  width: function width(_width2) {
    return isUnboundedValue('width', _width2) ? _width2 : undefined;
  },
  zIndex: function zIndex(_zIndex, _) {
    return _zIndex;
  }
};
var mapSingleInlineStyle = function mapSingleInlineStyle(key, value, options, currentMappings) {
  if (key === undefined || value === undefined || !(key in stylePropertyToMapper)) {
    return undefined;
  }
  var mapper = stylePropertyToMapper[key];
  return mapper(value, options, currentMappings);
};
var mapInlineStyles = function mapInlineStyles(props, options) {
  return Object.entries(props).reduce(function (acc, _ref7) {
    var _ref8 = _slicedToArray(_ref7, 2),
      key = _ref8[0],
      value = _ref8[1];
    var res = mapSingleInlineStyle(key, value, options, acc);
    if (res === undefined) return acc;
    if (_typeof(res) === 'object') return _objectSpread(_objectSpread({}, acc), res);
    if (key === 'start' || key === 'end' || key === 'DEPRECATED_gap') return acc;
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, key, res));
  }, {});
};
var mapSingleClassName = function mapSingleClassName(key, value, options) {
  if (key === undefined || !(key in propertyToMapper)) {
    return undefined;
  }
  var mapper = propertyToMapper[key];
  return mapper(value, options);
};
var mapClassNames = function mapClassNames(props, options) {
  return cx(Object.entries(propertyToMapper).reduce(function (acc, _ref9) {
    var _ref0 = _slicedToArray(_ref9, 2),
      key = _ref0[0],
      _ = _ref0[1];
    var result = mapSingleClassName(key, props[key], options);
    if (result === undefined) return acc;
    if (Array.isArray(result)) return [].concat(_toConsumableArray(acc), _toConsumableArray(result));
    acc.push(result);
    return acc;
  }, []), options.screen).trim();
};
var mapAttributes = function mapAttributes(_ref1, _ref10) {
  var type = _ref1.type,
    value = _ref1.value,
    attributes = _objectWithoutProperties(_ref1, _excluded);
  var isButton = _ref10.isButton,
    isInput = _ref10.isInput;
  var inputType = isInput ? type : undefined;
  var buttonType = isButton ? type || 'button' : undefined;
  var inputValue = isInput || isButton ? value : undefined;
  var filteredAttributes = Object.entries(attributes).reduce(function (acc, _ref11) {
    var _ref12 = _slicedToArray(_ref11, 2),
      key = _ref12[0],
      attributeValue = _ref12[1];
    if (key in propertyToMapper || key in stylePropertyToMapper) {
      return acc;
    }
    var typedKey = key;
    acc[typedKey] = attributeValue;
    return acc;
  }, {});
  return _objectSpread(_objectSpread({}, filteredAttributes), {}, {
    type: buttonType || inputType,
    value: inputValue
  });
};
var mapProps = function mapProps(_ref13) {
  var as = _ref13.as,
    props = _objectWithoutProperties(_ref13, _excluded2);
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    direction: Direction.LTR
  };
  var optionsWithIsLtr = _objectSpread(_objectSpread({}, options), {}, {
    isLtr: options.direction !== Direction.RTL,
    isButton: as === 'button',
    isInput: !!(as && ['input', 'textarea', 'select'].includes(as))
  });
  var className = mapClassNames(props, optionsWithIsLtr);
  var style = mapInlineStyles(props, optionsWithIsLtr);
  var attributes = mapAttributes(props, optionsWithIsLtr);
  return {
    className: className,
    style: style,
    attributes: attributes
  };
};
var useMappedProps = function useMappedProps(props) {
  var _useLocaleContext = useLocaleContext(),
    direction = _useLocaleContext.direction;
  var theme = useThemeContext();
  return mapProps(props, {
    direction: direction,
    theme: theme
  });
};
var mapperUtils = {
  mapProps: mapProps,
  useMappedProps: useMappedProps
};

export { mapProps, mapperUtils, useMappedProps };
//# sourceMappingURL=mappers.js.map
